<template>
  <div class="form-container">
    <h1 class="page-title">見積小分類マスタ編集</h1>

    <div v-if="!isAdmin" class="alert alert-danger">
      見積小分類の更新権限がありません。
      <router-link :to="listRoute">一覧へ戻る</router-link>
    </div>

    <template v-else>
      <div v-if="errorMessages.length" class="alert alert-danger">
        <div v-for="message in errorMessages" :key="message">{{ message }}</div>
      </div>

      <div v-if="loading" class="text-center py-5">読み込み中...</div>

      <template v-else-if="subcategory">
        <div v-if="Number(subcategory.del_flg) === 1" class="alert alert-warning">
          この見積小分類は削除済みです。再利用する場合は一覧画面から復元してください。
        </div>

        <EstimateSubcategoryForm
          :initial-data="subcategory"
          :business-classifications="businessClassifications"
          :errors="fieldErrors"
          :submitting="submitting"
          submit-label="更新"
          submit-button-class="btn-success"
          @submit="updateSubcategory"
          @cancel="router.push(listRoute)"
        />
      </template>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import EstimateSubcategoryForm from '@/components/estimateSubcategories/EstimateSubcategoryForm.vue';
import {
  getBusinessClassifications,
  getEstimateSubcategory,
  updateEstimateSubcategory
} from '@/api/axios';
import { extractApiErrors } from '@/utils/estimates';

const props = defineProps({
  id: {
    type: [String, Number],
    required: true
  }
});

document.title = '見積小分類マスタ編集';

const router = useRouter();
const route = useRoute();
const store = useStore();
const listRoute = computed(() => ({
  name: 'EstimateSubcategoryList',
  query: route.query
}));
const isAdmin = computed(() => [0, 999].includes(Number(store.state.userRole)));
const subcategory = ref(null);
const businessClassifications = ref([]);
const loading = ref(true);
const submitting = ref(false);
const errorMessages = ref([]);
const fieldErrors = ref({});

const loadData = async () => {
  loading.value = true;
  errorMessages.value = [];

  try {
    const [subcategoryResponse, classificationsResponse] = await Promise.all([
      getEstimateSubcategory(props.id),
      getBusinessClassifications({ deleted_status: 'active' })
    ]);
    subcategory.value = subcategoryResponse.data.estimate_subcategory;
    businessClassifications.value = classificationsResponse.data || [];

    const currentClassification = subcategory.value.business_classification;
    const containsCurrent = businessClassifications.value.some(
      classification => Number(classification.id) === Number(currentClassification.id)
    );
    if (!containsCurrent) {
      businessClassifications.value.push(currentClassification);
    }
  } catch (error) {
    errorMessages.value = extractApiErrors(error);
    subcategory.value = null;
  } finally {
    loading.value = false;
  }
};

const updateSubcategory = async (payload) => {
  submitting.value = true;
  errorMessages.value = [];
  fieldErrors.value = {};

  try {
    await updateEstimateSubcategory(props.id, payload);
    store.dispatch('setFlashMessage', {
      message: '見積小分類を更新しました',
      type: 'success'
    });
    await router.push(listRoute.value);
  } catch (error) {
    errorMessages.value = extractApiErrors(error);
    fieldErrors.value = error.response?.data?.errors || {};
  } finally {
    submitting.value = false;
  }
};

onMounted(() => {
  if (isAdmin.value) {
    loadData();
  } else {
    loading.value = false;
  }
});
</script>
